import React, { createContext, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage, setCurrentLanguage, LANGUAGES } from '../utils/languageUtils';

// Create the Language Context
const LanguageContext = createContext();

/**
 * Language Provider Component
 * Manages the global language state for the application
 */
export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setLanguage] = useState(getCurrentLanguage());

  /**
   * Change the application language
   * @param {string} languageCode - Language code (en, si, ta)
   */
  const changeLanguage = (languageCode) => {
    if (setCurrentLanguage(languageCode)) {
      setLanguage(languageCode);
      // Change i18next language
      i18n.changeLanguage(languageCode);
      // You can add additional side effects here
      // For example, triggering analytics, updating API preferences, etc.
      console.log(`Language changed to: ${languageCode}`);
    }
  };

  // Load language preference on mount and sync with i18next
  useEffect(() => {
    const savedLanguage = getCurrentLanguage();
    if (savedLanguage !== currentLanguage) {
      setLanguage(savedLanguage);
    }
    // Sync i18next with saved language
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    currentLanguage,
    changeLanguage,
    isEnglish: currentLanguage === LANGUAGES.ENGLISH,
    isSinhala: currentLanguage === LANGUAGES.SINHALA,
    isTamil: currentLanguage === LANGUAGES.TAMIL
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Custom hook to use the Language Context
 * @returns {Object} Language context value
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
